import React from 'react';

/**
 * 진입~청산 가격 스파크라인. 표의 한 행 안에서만 그린다.
 *
 * **행 높이를 절대 바꾸지 않는다**: 감싸는 td 가 position:relative 이고 이 svg 는
 * position:absolute 로 그 안을 채우므로 레이아웃 높이에 기여분이 0 이다. 차트가
 * 몇 점이든, 세로로 얼마나 출렁이든 행 높이는 다른 칼럼(3줄 스택)이 정한다.
 *
 * **Y축은 ±10% 고정**(진입가/프리 기준가 대칭). 10%를 넘는 점은 viewBox 밖으로
 * 나가 옆 행을 침범한다(overflow:visible). 예전엔 편차가 커질수록 10→50% 계단으로
 * 밴드를 넓혀 같은 32px 안에 구겨 넣었는데, 그러면 큰 움직임이 작아 보였다.
 *
 * **가로축(시간)은 고정**: 데이터 min~max 로 늘이지 않는다. tStart → tStart+
 * windowMin 이 항상 차트 전체 폭이다. 진입 후 1분만 지났으면 왼쪽 1/windowMin
 * 구간에만 선이 그려지고, 나머지 오른쪽은 비어 있다.
 *
 * **프리차트(어두운 선)**: 청캐 WATCH · 업숏 진입대기. 돈이 걸리기 전 구간.
 * 진입 뒤에는 본차트(초록/빨강)가 같은 축에 덧그려진다.
 *
 * **포스트차트(어두운 꼬리)**: 수동·외부청산 뒤 홀드 만료까지의 반사실 가격.
 * 색 선은 실제 보유(진입→청산)만, 그 뒤는 프리와 같은 muted.
 *
 * **색은 방향에 따라 뒤집힌다** (`isLong`). 숏은 진입가 아래가 이익(초록), 롱은
 * 위가 이익이다. 2026-08-14 이전에는 숏 고정이라 **지고 있는 롱이 초록**으로
 * 그려졌다 -- 후보-롱 EDEN 04:09(스탑까지 내려가 -3.16%)가 초록이었다.
 * 색만 방향을 타고 Y축 배치는 그대로다(기준가가 늘 정중앙).
 *
 * 기준가 점선은 **항상 세로 정중앙**에 온다. 이게 이 차트의 요점이다: 데이터
 * 범위에 맞춰 스케일하면 점선 위치가 행마다 달라져 매번 다시 읽어야 하지만,
 * 고정하면 선이 위 = 손실 / 아래 = 이익로 즉시 읽힌다.
 */
export type SparkPoint = [number, number];   // [epoch_ms, close]

const GREEN = '#3fb950';
const RED = '#f85149';
const MUTED = '#8b7d5a';
/** WATCH / 진입대기 프리차트 — 본차트보다 어둡게. */
const PRE_MUTED = '#4a4336';
const DEFAULT_WINDOW_MIN = 30;

/** Y축 반경(기준가 대비). 10% = 셀 상·하단. 초과분은 옆 행으로 넘친다. */
export const SPARK_FIXED_HALF_PCT = 0.10;

/** @deprecated 계단 밴드는 ±10% 고정으로 대체됨. 테스트·호환용으로 남김. */
export const SPARK_HALF_TIERS = [0.10, 0.15, 0.20, 0.25, 0.30, 0.35, 0.40, 0.45, 0.50] as const;

/** @deprecated sparkPriceRadius 가 고정 10%를 씀. */
export function sparkMinHalfPct(
  _entryPx: number,
  _pathDev: number,
  _exitDev: number,
): number {
  return SPARK_FIXED_HALF_PCT;
}

/** 기준가 ±10% 반경(가격 단위). */
export function sparkPriceRadius(entryPx: number): number {
  return entryPx * SPARK_FIXED_HALF_PCT;
}

/**
 * 점선·Y축 기준가. entry → pre → 시리즈 첫 점 순.
 *
 * 기준가가 비면 (1) 가운데 점선이 안 그려지고 (2) min/max 오토스케일로
 * 1초 꼬리 노이즈가 셀 높이를 채워 **상하로만** 출렁인다 — 가로는 홀드창이라
 * 1초가 픽셀 미만이다(2026-08-26, 행 첫 표시).
 */
export function sparkCenterPx(
  entryPx: number | undefined,
  prePx: number | undefined,
  series: SparkPoint[][],
): number | undefined {
  if (entryPx != null && entryPx > 0) return entryPx;
  if (prePx != null && prePx > 0) return prePx;
  for (const pts of series) {
    for (const p of pts) {
      if (p[1] > 0) return p[1];
    }
  }
  return undefined;
}

/** 청산 ● 가 점선에서 차트 반쪽 높이의 몇 % 아래인지 (0=점선, 100=바닥=−10%). */
export function sparkExitDepthPct(
  entryPx: number,
  exitPx: number,
): number {
  const r = sparkPriceRadius(entryPx);
  return (Math.abs(exitPx - entryPx) / r) * 100;
}

/**
 * 선·끝점 색. **방향을 타는 유일한 계산이다.**
 *
 * 숏은 진입가 아래가 이익, 롱은 위가 이익이다. 2026-08-14 이전에는 숏으로 고정돼
 * 있어서 **지고 있는 롱이 초록**으로 그려졌다 — 후보-롱 EDEN 04:09 은 진입
 * 0.06073 에서 스탑 0.05887 까지 내려가 -3.16% 였는데 화면은 초록이었다.
 */
export function sparkStrokeColor(
  entryPx: number | undefined,
  lastPx: number,
  isLong: boolean,
): string {
  if (entryPx == null) return MUTED;
  const win = isLong ? lastPx >= entryPx : lastPx <= entryPx;
  return win ? GREEN : RED;
}

/** 고정 시간창 [tStart, tStart+windowMs] 안에서 t 의 x (0..W). */
export function sparkTimeX(
  t: number,
  tStart: number,
  windowMs: number,
  W = 100,
): number {
  if (!(windowMs > 0)) return 0;
  const x = ((t - tStart) / windowMs) * W;
  return Math.max(0, Math.min(W, x));
}

function pathD(
  points: SparkPoint[],
  x: (t: number) => number,
  y: (v: number) => number,
): string {
  return points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p[0]).toFixed(2)},${y(p[1]).toFixed(2)}`)
    .join('');
}

export function BinanceEntrySparkline({
  points,
  entryPx,
  exitPx: _exitPx,
  prePoints,
  prePx,
  postPoints,
  windowMin = DEFAULT_WINDOW_MIN,
  tStartMs,
  isLong = false,
  liveTip = false,
}: {
  points?: SparkPoint[] | null;
  entryPx?: number;
  /** 청산가(호환용 — Y 스케일은 더 이상 쓰지 않음). */
  exitPx?: number;
  /** WATCH / 진입대기 프리차트(어두운 선). */
  prePoints?: SparkPoint[] | null;
  /** 프리차트 기준가(진입 전 점선). 진입 생기면 entryPx 가 이긴다. */
  prePx?: number;
  /** 수동·외부청산 뒤 홀드 끝까지의 어두운 꼬리. */
  postPoints?: SparkPoint[] | null;
  /** 가로축 전체 길이(분). hold / watch / delay. 데이터가 짧아도 이 폭을 채우지 않는다. */
  windowMin?: number;
  /** 가로축 시작(epoch ms). 없으면 프리·본차트 첫 점. */
  tStartMs?: number;
  /** 롱 레인인가(abort→long · 후보-롱). 색의 부호만 뒤집는다. 기본은 숏. */
  isLong?: boolean;
  /** 1초 mark 꼬리가 살아 있으면 끝점을 노란 점으로 강조 (2026-08-25). */
  liveTip?: boolean;
}): React.ReactElement | null {
  const entryPts = points && points.length >= 2 ? points : null;
  const prePts = prePoints && prePoints.length >= 2 ? prePoints : null;
  const postPts = postPoints && postPoints.length >= 2 ? postPoints : null;
  if (!entryPts && !prePts) return null;

  const W = 100;
  const H = 32;
  const PAD_L = 4;
  /** 2026-09-04: 8→2. Chart 는 표의 마지막 칸이라 끝 ● 가 옆 칸을 침범할 일이
   *  없고(td overflow:visible), 유저가 스파크라인을 표 오른쪽 끝에 붙이길 원했다. */
  const PAD_R = 2;
  const plotW = W - PAD_L - PAD_R;
  const tStart = tStartMs
    ?? (prePts ? prePts[0][0] : entryPts![0][0]);
  const windowMs = Math.max(1, windowMin) * 60_000;

  // 기준가: 진입가 → 프리(알림/감시)가 → 시리즈 첫 점. 항상 ±10% 고정 —
  // 오토스케일 폴백은 쓰지 않는다(위 sparkCenterPx 주석).
  const centerPx = sparkCenterPx(
    entryPx, prePx,
    [prePts ?? [], entryPts ?? [], postPts ?? []],
  );
  if (centerPx == null) return null;
  const r = sparkPriceRadius(centerPx);
  const lo = centerPx - r;
  const hi = centerPx + r;

  const x = (t: number) => PAD_L + sparkTimeX(t, tStart, windowMs, plotW);
  // 클램프하지 않는다 — ±10% 밖은 셀 밖으로 그려 옆 행을 침범한다.
  const y = (v: number) => H - ((v - lo) / (hi - lo)) * H;

  const last = entryPts ? entryPts[entryPts.length - 1]
    : prePts![prePts!.length - 1];
  const color = entryPts
    ? sparkStrokeColor(entryPx, last[1], isLong)
    : PRE_MUTED;
  const postLast = postPts ? postPts[postPts.length - 1] : null;

  return (
    <svg
      className="binance-alert-spark"
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height="100%"
      preserveAspectRatio="none"
      aria-hidden="true"
      overflow="visible"
    >
      <line
        x1={PAD_L} x2={W - PAD_R} y1={y(centerPx)} y2={y(centerPx)}
        stroke={MUTED} strokeWidth={0.5} strokeDasharray="2 2" vectorEffect="non-scaling-stroke"
      />
      {prePts && (
        <path
          d={pathD(prePts, x, y)}
          fill="none"
          stroke={PRE_MUTED}
          strokeWidth={1.1}
          vectorEffect="non-scaling-stroke"
        />
      )}
      {entryPts && (
        <>
          <path
            d={pathD(entryPts, x, y)}
            fill="none"
            stroke={color}
            strokeWidth={1.2}
            vectorEffect="non-scaling-stroke"
          />
          <circle
            cx={x(last[0])}
            cy={y(last[1])}
            r={liveTip ? 2.4 : 1.6}
            fill={liveTip ? '#f0b90b' : color}
            stroke={liveTip ? '#0d1117' : undefined}
            strokeWidth={liveTip ? 0.6 : undefined}
            vectorEffect="non-scaling-stroke"
          />
        </>
      )}
      {postPts && (
        <path
          d={pathD(postPts, x, y)}
          fill="none"
          stroke={PRE_MUTED}
          strokeWidth={1.1}
          vectorEffect="non-scaling-stroke"
        />
      )}
      {!entryPts && prePts && (
        <circle
          cx={x(last[0])}
          cy={y(last[1])}
          r={liveTip ? 2.4 : 1.4}
          fill={liveTip ? '#f0b90b' : PRE_MUTED}
          stroke={liveTip ? '#0d1117' : undefined}
          strokeWidth={liveTip ? 0.6 : undefined}
          vectorEffect="non-scaling-stroke"
        />
      )}
      {postLast && (
        <circle
          cx={x(postLast[0])}
          cy={y(postLast[1])}
          r={1.4}
          fill={PRE_MUTED}
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  );
}
